/**
 * The data behind one profile card.
 *
 * Both queries are `enabled` on the id AND on the card actually being open —
 * a popover mounts its content only while open, but a caller that keeps the
 * card mounted (a details pane) must not turn every avatar in a timeline into
 * two HTTP requests.
 */
import { useQuery } from '@tanstack/react-query';
import type { UserId } from '@cyphex/types/common';
import type { User } from '@cyphex/types/identity';
import { peopleApi, type PersonAvailability } from '../api/peopleApi';
import { PEOPLE_KEYS } from '../api/queryKeys';

/** A profile changes about as often as a job title does. */
const PROFILE_STALE_MS = 5 * 60 * 1000;
/** A calendar changes constantly; a minute is short enough to feel live. */
const AVAILABILITY_STALE_MS = 60 * 1000;

export interface PersonProfile {
  readonly user: User | null;
  readonly availability: PersonAvailability | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

export function usePersonProfile(userId: UserId | null, enabled = true): PersonProfile {
  const active = enabled && userId !== null;

  const profile = useQuery({
    queryKey: PEOPLE_KEYS.profile(userId ?? ('' as UserId)),
    queryFn: ({ signal }) => peopleApi.getUser(userId as UserId, signal),
    enabled: active,
    staleTime: PROFILE_STALE_MS,
  });

  // Availability is ADDITIVE — a workspace with no calendar events, or a person
  // who never set working hours, answers with an empty payload and the card
  // simply drops those lines. A failure here must therefore never take the
  // identity half of the card down with it, which is why it is a second query
  // and its error is not surfaced as `isError`.
  const availability = useQuery({
    queryKey: PEOPLE_KEYS.availability(userId ?? ('' as UserId)),
    queryFn: ({ signal }) => peopleApi.getAvailability(userId as UserId, signal),
    enabled: active,
    staleTime: AVAILABILITY_STALE_MS,
    retry: false,
  });

  return {
    user: profile.data ?? null,
    availability: availability.data ?? null,
    isLoading: active && profile.isPending,
    isError: profile.isError,
  };
}
